import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useOnboardingWizard } from "../OnboardingWizardProvider";
import { Loader2, CheckCircle2, SkipForward } from "lucide-react";

export function PollingStep() {
  const { t } = useTranslation();
  const {
    stepConfig,
    formData,
    executeAction,
    goNext,
    markStepComplete,
    currentStep,
    onStepComplete,
  } = useOnboardingWizard();

  const [found, setFound] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!stepConfig.pollAction) return;

    const poll = async () => {
      const result = await executeAction(stepConfig.pollAction!, { formData });
      if (result.success) {
        setFound(true);
        if (intervalRef.current) clearInterval(intervalRef.current);
        markStepComplete(currentStep);
        onStepComplete(currentStep);
        setTimeout(() => goNext(), 2000);
      }
    };

    // Poll immediately then on interval
    poll();
    intervalRef.current = setInterval(
      poll,
      stepConfig.pollIntervalMs || 5000
    );

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [
    stepConfig,
    formData,
    executeAction,
    goNext,
    markStepComplete,
    currentStep,
    onStepComplete,
  ]);

  const handleSkip = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    markStepComplete(currentStep);
    onStepComplete(currentStep);
    goNext();
  };

  return (
    <div className="text-center space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">
          {t(stepConfig.title)}
        </h2>
        {stepConfig.subtitle && (
          <p className="text-muted-foreground">{t(stepConfig.subtitle)}</p>
        )}
      </div>

      <div className="py-8">
        {!found ? (
          <div className="space-y-4">
            <div className="relative mx-auto w-16 h-16">
              <Loader2 className="h-16 w-16 animate-spin text-nara-orange/30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-3 w-3 rounded-full bg-nara-orange animate-pulse" />
              </div>
            </div>
            <p className="text-muted-foreground">
              {stepConfig.waitingText
                ? t(stepConfig.waitingText)
                : t("common.loading")}
            </p>
          </div>
        ) : (
          <div className="space-y-4 animate-in fade-in duration-500">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <p className="text-foreground font-medium">
              {stepConfig.successText
                ? t(stepConfig.successText)
                : t("common.success")}
            </p>
          </div>
        )}
      </div>

      {/* Skip button */}
      {!found && stepConfig.skipLabel && (
        <button
          onClick={handleSkip}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <SkipForward className="h-4 w-4" />
          {t(stepConfig.skipLabel)}
        </button>
      )}
    </div>
  );
}
